import React, { useState, useEffect } from 'react';
import './App.css';
import FAQ from './components/zonezapp/FAQs';
import Download from './components/zonezapp/Download';
import ContactUs from './components/zonezapp/ContactUs';
import HowItWorks from './components/zonezapp/HowItWorks';
import UsagePolicies from './components/zonezapp/UsagePolicies';
import ZoneZappLandingPage from './components/zonezapp/Zonezapp';
import TermsAndConditions from './components/zonezapp/Terms&Conditions';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import SinglePager from './components/parent-components/single-pager/SinglePager';

import PLAY_VERIFY from './components/play-verify/play-verify';
import ZoneZappPromo from './components/zonezapp/Business';
import LoginPage from './webapp/LoginPage';
import MapPage from './webapp/MapPage';
import PostLoginHome from './webapp/PostLoginHome';
import NewHome from './webapp/NewHome';
import VideoReels from './webapp/VideoReels';
import VisitorCenter from './components/visitor-center/VisitorCenter';

// SEO-optimized content pages
import LocalEventsAlertsApp from './components/seo-pages/LocalEventsAlertsApp';
import NeighborhoodSafetyApp from './components/seo-pages/NeighborhoodSafetyApp';
import CommunityUpdatesApp from './components/seo-pages/CommunityUpdatesApp';
import MobileAppLanding from './components/product-pages/MobileAppLanding';


// Minimal client-side route guard for the PostLoginHome route.
// Checks for a locally-stored token and optionally validates it via /api/auth/me.
function RequireAuth({ children }) {
  const [checking, setChecking] = React.useState(false);
  // quick sync check for token presence
  let token = null;
  try { token = localStorage.getItem('zz_token'); } catch (e) { token = null; }

  React.useEffect(() => {
    let mounted = true;
    if (!token) return () => {};
    // Optionally verify token server-side to avoid stale tokens allowing access.
    setChecking(true);
    fetch('/api/auth/me', { method: 'GET', headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then(r => {
        if (!mounted) return;
        if (r.status === 200) {
          setChecking(false);
        } else {
          try { localStorage.removeItem('zz_token'); } catch (e) {}
          setChecking(false);
        }
      })
      .catch(() => { if (mounted) setChecking(false); });
    return () => { mounted = false; };
  }, []);

  if (!token) return <Navigate to="/webapp/login" replace />;
  if (checking) return null; // or a small loader component
  return children;
}

const App = () => {

  // Function to get initial theme
  const getInitialTheme = () => {
    // Check if user has a manual preference
    const savedTheme = localStorage.getItem("theme");
    const hasManualPreference = localStorage.getItem("themeManuallySet");
    
    if (savedTheme && hasManualPreference === "true") {
      // User manually set theme before
      console.log("🎨 Using manual theme preference:", savedTheme);
      return savedTheme;
    }
    
    // Auto-detect from system
    const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const detectedTheme = prefersDark ? "dark" : "light";
    console.log("🎨 Auto-detected system theme:", detectedTheme);
    return detectedTheme;
  };

  const [theme, setTheme] = useState(getInitialTheme);
  // Initialize activeSection as 'home' to make "Home" active on load
  const [activeSection, setActiveSection] = useState("home");
  // Track if the user clicked a sitemap (Track manual selection)
  const [manualOverride, setManualOverride] = useState(false);
  // Initialize openModal to open & close the popup model
  const [openModal, setOpenModal] = useState(false);

  // One-time migration: Clear old theme preferences to enable auto-detection
  useEffect(() => {
    const migrationVersion = localStorage.getItem("themeMigrationV2");
    if (!migrationVersion) {
      // Clear old theme settings to force auto-detection
      const hadOldTheme = localStorage.getItem("theme");
      if (hadOldTheme && !localStorage.getItem("themeManuallySet")) {
        localStorage.removeItem("theme");
      }
      localStorage.setItem("themeMigrationV2", "true");
      // Force re-detect
      const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
      const detectedTheme = prefersDark ? "dark" : "light";
      setTheme(detectedTheme);
      document.body.className = detectedTheme;
    }
  }, []);

  // Auto-detect and sync theme
  useEffect(() => {
    // Apply theme immediately
    document.body.className = theme;
    
    // Listen for system theme changes (only if not manually set)
    const hasManualPreference = localStorage.getItem("themeManuallySet") === "true";
    
    if (!hasManualPreference) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      
      const handleSystemThemeChange = (e) => {
        const newTheme = e.matches ? "dark" : "light";
        setTheme(newTheme);
        document.body.className = newTheme;
        // Save the auto-detected theme but don't mark as manual
        localStorage.setItem("theme", newTheme);
      };

      // Add listener
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleSystemThemeChange);
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleSystemThemeChange);
      }
      
      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener("change", handleSystemThemeChange);
        } else {
          mediaQuery.removeListener(handleSystemThemeChange);
        }
      };
    }
  }, [theme]);

  // Toggle Theme (manual override)
  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.body.className = newTheme;
    localStorage.setItem("theme", newTheme);
    localStorage.setItem("themeManuallySet", "true"); // Mark as manually set
  };

  // Model Control
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  // Navigation Tracking
  useEffect(() => {
    const handleScroll = () => {
      if (manualOverride) return; // Skip scroll updates during manual override

      const sections = document.querySelectorAll('section');
      let currentSection = 'home'; // Default section if none are active

      const scrollBottomThreshold = 50; // Define how close to the bottom is considered "bottom"

      // Check if near the bottom of the page
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - scrollBottomThreshold
      ) {
        currentSection = 'contact-us'; // Set "Contact" as active when at the bottom
      } else {
        // Regular section logic
        sections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          if (
            rect.top <= window.innerHeight / 2 &&
            rect.bottom >= window.innerHeight / 2
          ) {
            currentSection = section.id;
          }
        });
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [manualOverride]);

  // Ensure the active section updates on page load or direct navigation
  useEffect(() => {
    const currentHash = window.location.hash.replace('#', '') || 'home'; // Default to 'home' if no hash
    const targetElement = document.getElementById(currentHash);
    if (targetElement) {
      setActiveSection(currentHash); // Manually set the active section
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleNavClick = (id) => {
    setActiveSection(id);
    setManualOverride(true);

    // Scroll smoothly to the section
    document.getElementById(id).scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Re-enable scroll updates after 1 second
    setTimeout(() => setManualOverride(false), 1000);
  };


  return (

    <Router>
      <div className='App'>
        <Routes>
          <Route path="/" element={ <SinglePager handleOpenModal={handleOpenModal} toggleTheme={toggleTheme} theme={theme} activeSection={activeSection} handleNavClick={handleNavClick} /> } />
          <Route path="/faqs" element={<FAQ toggleTheme={toggleTheme} />} />
          <Route path="/appflow" element={<HowItWorks toggleTheme={toggleTheme} />} />
          <Route path="/contact-us" element={<ContactUs toggleTheme={toggleTheme} />} />
          <Route path="/zonezapp" element={<ZoneZappLandingPage toggleTheme={toggleTheme} />} />
          <Route path="/usage-policies" element={<UsagePolicies toggleTheme={toggleTheme} />} />
          <Route path="/terms&conditions" element={<TermsAndConditions toggleTheme={toggleTheme} />} />
          <Route path="/play-verify" element={<PLAY_VERIFY />} />
          <Route path="/download" element={<Download toggleTheme={toggleTheme} />} />
          <Route path="/business" element={<ZoneZappPromo toggleTheme={toggleTheme} />} />
          
          {/* Product pages */}
          <Route path="/mobile-app" element={<MobileAppLanding toggleTheme={toggleTheme} theme={theme} />} />
          
          {/* SEO-optimized content pages */}
          <Route path="/local-events-alerts-app" element={<LocalEventsAlertsApp toggleTheme={toggleTheme} />} />
          <Route path="/neighborhood-safety-alert-app" element={<NeighborhoodSafetyApp toggleTheme={toggleTheme} />} />
          <Route path="/best-local-news-updates-app" element={<CommunityUpdatesApp toggleTheme={toggleTheme} />} />
          
          {/* Web app routes */}
          <Route path="/webapp/login" element={<LoginPage />} />
          <Route path="/webapp/map" element={<MapPage />} />
          <Route path="/webapp/home" element={<RequireAuth><NewHome /></RequireAuth>} />
          <Route path="/webapp/home/full" element={<RequireAuth><PostLoginHome /></RequireAuth>} />
          <Route path="/webapp/reels" element={<RequireAuth><VideoReels /></RequireAuth>} />
          <Route path="/webapp/visitor-center" element={<VisitorCenter />} />
          <Route path="/visitor-center" element={<VisitorCenter />} />
        </Routes>
      </div>
    </Router>

  )
};

export default App;
